import {
  buildPrintSchedule,
  buildSupportedPersonSchedule,
  printStylesheet,
  type PrintScheduleSource,
  type PrintScheduleView,
  type SupportedPersonScheduleView,
} from "@herefor/print";
import {
  PRACTICAL_UPDATE_MESSAGES,
  PRINT_SCHEDULE_MESSAGES as COPY,
  SUPPORTED_PERSON_PRINT_CONTACT_MESSAGES,
  SUPPORTED_PERSON_PRINT_MESSAGES as THEIRS,
} from "@herefor/product-language";
import { useState } from "react";

export interface PrintScheduleProps {
  readonly source: PrintScheduleSource;
  readonly onPrint?: () => void;
}

/**
 * The print route's two documents (PRT-001 and PRT-002), behind one audience choice.
 *
 * They are separate papers, not two settings on one — the family sheet's detail and
 * contact options are meaningless on the supported person's sheet, and the "who to
 * check with" line is meaningless on the family's — so the controls that do not
 * apply are absent rather than disabled. The family sheet stays selected first,
 * because it is the one an organiser opens the page for.
 */
export function PrintSchedule({ source, onPrint = defaultPrint }: PrintScheduleProps) {
  const [audience, setAudience] = useState<"family" | "supportedPerson">("family");
  const [mode, setMode] = useState<"discreet" | "full">("discreet");
  const [periodDays, setPeriodDays] = useState<7 | 14>(7);
  const [includeContact, setIncludeContact] = useState(false);
  const [contactPersonName, setContactPersonName] = useState(() => source.contactPersonName ?? "");
  const forFamily = audience === "family";
  const schedule = buildPrintSchedule(source, { mode, periodDays, includeContact });
  const theirSchedule = buildSupportedPersonSchedule(source, { periodDays, contactPersonName });

  return (
    <section className="print-route" aria-labelledby="print-heading">
      <style>{printStylesheet}</style>
      <header className="print-route__intro">
        <h1 id="print-heading">{COPY.heading}</h1>
        <p>{COPY.introduction}</p>
      </header>

      <div className="print-controls" aria-label={COPY.heading}>
        <fieldset>
          <legend>{THEIRS.audienceLegend}</legend>
          <PrintOption
            checked={forFamily}
            hint={THEIRS.familyHint}
            label={THEIRS.familyLabel}
            name="print-audience"
            onChange={() => {
              setAudience("family");
            }}
            value="family"
          />
          <PrintOption
            checked={!forFamily}
            hint={THEIRS.supportedPersonHint}
            label={THEIRS.supportedPersonLabel}
            name="print-audience"
            onChange={() => {
              setAudience("supportedPerson");
            }}
            value="supportedPerson"
          />
        </fieldset>

        {forFamily ? (
          <fieldset>
            <legend>{COPY.detailLegend}</legend>
            <PrintOption
              checked={mode === "discreet"}
              hint={COPY.discreetHint}
              label={COPY.discreetLabel}
              name="print-detail"
              onChange={() => {
                setMode("discreet");
              }}
              value="discreet"
            />
            <PrintOption
              checked={mode === "full"}
              hint={COPY.fullHint}
              label={COPY.fullLabel}
              name="print-detail"
              onChange={() => {
                setMode("full");
              }}
              value="full"
            />
          </fieldset>
        ) : null}

        <fieldset>
          <legend>{COPY.periodLegend}</legend>
          <PrintOption
            checked={periodDays === 7}
            hint={COPY.sevenDaysHint}
            label={COPY.sevenDaysLabel}
            name="print-period"
            onChange={() => {
              setPeriodDays(7);
            }}
            value="7"
          />
          <PrintOption
            checked={periodDays === 14}
            hint={COPY.fourteenDaysHint}
            label={COPY.fourteenDaysLabel}
            name="print-period"
            onChange={() => {
              setPeriodDays(14);
            }}
            value="14"
          />
        </fieldset>

        {forFamily && mode === "full" && source.contactText !== null ? (
          <label className="print-controls__contact-option">
            <input
              checked={includeContact}
              onChange={(event) => {
                setIncludeContact(event.currentTarget.checked);
              }}
              type="checkbox"
            />
            <span>
              {COPY.includeContactLabel}
              <span className="print-controls__hint">{COPY.includeContactHint}</span>
            </span>
          </label>
        ) : null}

        {forFamily ? null : (
          <label className="print-controls__contact-option">
            <span>
              {THEIRS.contactPersonLabel}
              <span className="print-controls__hint">
                {SUPPORTED_PERSON_PRINT_CONTACT_MESSAGES.overrideHint}
              </span>
            </span>
            <input
              onChange={(event) => {
                setContactPersonName(event.currentTarget.value);
              }}
              type="text"
              value={contactPersonName}
            />
          </label>
        )}

        <button className="print-controls__action" onClick={onPrint} type="button">
          {forFamily ? COPY.printAction : THEIRS.printAction}
        </button>
      </div>

      {forFamily ? (
        <FamilySchedule schedule={schedule} />
      ) : (
        <SupportedPersonSchedule schedule={theirSchedule} />
      )}
    </section>
  );
}

/** The household paper (PRT-001): coverage, helpers, and detail behind two choices. */
function FamilySchedule({ schedule }: { readonly schedule: PrintScheduleView }) {
  const firstDay = schedule.days[0];
  const lastDay = schedule.days.at(-1);

  return (
    <article
      className="print-schedule"
      data-print-audience="family"
      data-print-days={String(schedule.periodDays)}
      data-print-mode={schedule.mode}
    >
      <header className="print-schedule__header">
        <h1>{COPY.scheduleHeading(schedule.planName)}</h1>
        {firstDay === undefined || lastDay === undefined ? null : (
          <p className="print-schedule__period">
            {COPY.period(schedule.periodDays, firstDay.dateLabel, lastDay.dateLabel)}
          </p>
        )}
        {/* THE UNCONFIRMED SPELLING, ON THE PAGE (bead hf-86o). The settings screen
              can put a warning beside a placeholder name; a printout on a kitchen wall
              carries only what was printed on it. In both modes, for the same reason
              the notice exists at all. */}
        {schedule.supportedPersonNotice === null ? null : (
          <p className="print-schedule__notice">{schedule.supportedPersonNotice}</p>
        )}
        {schedule.contactText === null ? null : (
          <p className="print-schedule__contact">
            <strong>{COPY.contactHeading}</strong>
            <br />
            {schedule.contactText}
          </p>
        )}
      </header>

      <ol className="print-schedule__days">
        {schedule.days.map((day) => (
          <li className="print-day" data-print-date={day.localDate} key={day.localDate}>
            <h2>{day.dateLabel}</h2>
            {day.emptyLabel === null ? null : <p>{day.emptyLabel}</p>}
            <ul className="print-day__items">
              {day.events.map((event) => (
                <li className="print-item" key={event.id}>
                  <h3>{event.title}</h3>
                  <div className="print-item__facts">
                    {event.windowLabel === null ? null : <span>{event.windowLabel}</span>}
                    {event.locationLabel === null ? null : <span>{event.locationLabel}</span>}
                    {event.stateLabel === null ? null : (
                      <span className="print-state">{event.stateLabel}</span>
                    )}
                  </div>
                  <ul className="print-item__needs">
                    {event.needs.map((need) => (
                      <PrintNeed headingLevel={4} key={need.id} need={need} />
                    ))}
                  </ul>
                </li>
              ))}
              {day.standaloneNeeds.map((need) => (
                <PrintNeed headingLevel={3} key={need.id} need={need} />
              ))}
            </ul>
          </li>
        ))}
      </ol>

      <footer className="print-schedule__freshness">
        <p>{schedule.generatedAtLabel}</p>
        <p>{schedule.freshnessWarning}</p>
      </footer>
    </article>
  );
}

/**
 * The supported person's own paper (PRT-002, PRT-004; plan §20.4).
 *
 * Everything absent from this component is absent from the document: there is no
 * state chip, no coverage count, no provenance line and no practical-update list to
 * render, because `buildSupportedPersonSchedule` never put them in the view. The
 * markup cannot leak what it was not given, which is the point of doing the
 * minimisation in the package rather than here.
 */
function SupportedPersonSchedule({ schedule }: { readonly schedule: SupportedPersonScheduleView }) {
  const firstDay = schedule.days[0];
  const lastDay = schedule.days.at(-1);

  return (
    <article
      className="print-schedule print-schedule--supported"
      data-print-audience="supportedPerson"
      data-print-days={String(schedule.periodDays)}
    >
      <header className="print-schedule__header">
        <h1>{THEIRS.onThePage.heading(schedule.supportedPersonName)}</h1>
        {firstDay === undefined || lastDay === undefined ? null : (
          <p className="print-schedule__period">
            {THEIRS.onThePage.period(schedule.periodDays, firstDay.dateLabel, lastDay.dateLabel)}
          </p>
        )}
        {/* The same unconfirmed-spelling notice the family sheet carries (bead hf-86o).
            This page puts her name in its largest type, so if anywhere needs it, here. */}
        {schedule.supportedPersonNotice === null ? null : (
          <p className="print-schedule__notice">{schedule.supportedPersonNotice}</p>
        )}
      </header>

      <ol className="print-schedule__days">
        {schedule.days.map((day) => (
          <li className="print-day" data-print-date={day.localDate} key={day.localDate}>
            <h2>{day.dateLabel}</h2>
            {day.emptyLabel === null ? null : <p>{day.emptyLabel}</p>}
            <ul className="print-day__items">
              {day.entries.map((entry) => (
                <li
                  className={`print-entry print-entry--${entry.kind}`}
                  data-entry-kind={entry.kind}
                  key={`${entry.kind}-${entry.id}`}
                >
                  <h3>{entry.title}</h3>
                  <div className="print-entry__facts">
                    {entry.windowLabel === null ? null : <span>{entry.windowLabel}</span>}
                    {entry.locationLabel === null ? null : <span>{entry.locationLabel}</span>}
                  </div>
                  {entry.people.length === 0 ? null : (
                    <>
                      <p>{THEIRS.onThePage.whoIsComing}</p>
                      <ul className="print-entry__people">
                        {entry.people.map((name, index) => (
                          <li key={`${name}-${String(index)}`}>{name}</li>
                        ))}
                      </ul>
                    </>
                  )}
                  {entry.isBeingChecked ? <p>{THEIRS.onThePage.stillBeingChecked}</p> : null}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>

      <p className="print-schedule__check-with">
        {schedule.contactPersonName === null
          ? THEIRS.onThePage.checkWithWhoeverBroughtIt
          : THEIRS.onThePage.checkWith(schedule.contactPersonName)}
      </p>

      <footer className="print-schedule__freshness">
        <p>{schedule.generatedAtLabel}</p>
        <p>{schedule.freshnessWarning}</p>
      </footer>
    </article>
  );
}

function PrintOption({
  checked,
  hint,
  label,
  name,
  onChange,
  value,
}: {
  readonly checked: boolean;
  readonly hint: string;
  readonly label: string;
  readonly name: string;
  readonly onChange: () => void;
  readonly value: string;
}) {
  return (
    <label>
      <input checked={checked} name={name} onChange={onChange} type="radio" value={value} />
      <span>
        {label}
        <span className="print-controls__hint">{hint}</span>
      </span>
    </label>
  );
}

function PrintNeed({
  headingLevel,
  need,
}: {
  readonly headingLevel: 3 | 4;
  readonly need: ReturnType<typeof buildPrintSchedule>["days"][number]["standaloneNeeds"][number];
}) {
  const Heading = headingLevel === 3 ? "h3" : "h4";
  return (
    <li className="print-need">
      <Heading>{need.label}</Heading>
      <div className="print-need__facts">
        <span>{need.windowLabel}</span>
        <span className="print-state">{need.stateLabel}</span>
        {need.coverageCountLabel === null ? null : <span>{need.coverageCountLabel}</span>}
      </div>
      {need.responsible.length === 0 ? null : (
        <div>
          <p>{COPY.helpersHeading}</p>
          <ul className="print-need__helpers">
            {need.responsible.map((person, index) => (
              <li key={`${person.displayName}-${String(index)}`}>
                {person.provenance ?? person.displayName}
              </li>
            ))}
          </ul>
        </div>
      )}
      {need.practicalUpdates.length === 0 ? null : (
        <div className="print-need__updates">
          <p>{PRACTICAL_UPDATE_MESSAGES.printHeading}</p>
          <ul>
            {need.practicalUpdates.map((update, index) => (
              <li key={`${update.provenance}-${String(index)}`}>
                {update.text} — {update.provenance}
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}

function defaultPrint(): void {
  window.print();
}
